import { useEffect, useState } from "react";
import { Package, Plus, AlertTriangle, Minus, X, Share2, TrendingUp, Copy, LineChart } from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { shareToWhatsApp } from "@/lib/share";
import { useT } from "@/lib/i18n";

export default function Inventory() {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const [data, setData] = useState({ items: [], low_stock: [] });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [forecastFor, setForecastFor] = useState(null);

  const load = async () => {
    try {
      const [inv, sug] = await Promise.all([
        api.get("/inventory"),
        api.get("/insights/purchase-suggestions").catch(() => ({ data: null })),
      ]);
      setData(inv.data);
      setSuggestions(sug.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Iter33 live sync — refetch when a WhatsApp / mobile write hits inventory.
    const onLive = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "inventory" || kind === "brain_sync" || kind === "transaction") {
        load();
      }
    };
    window.addEventListener("kobolog:live", onLive);
    return () => window.removeEventListener("kobolog:live", onLive);
  }, []);

  const updateStock = async (id, delta) => {
    try {
      await api.put(`/inventory/${id}/stock`, { delta });
      await load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-6 text-center text-kudi-ink/50">{t("loadingInventory")}</div>;

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">{t("stockTitle")}</div>
          <h1 className="font-heading text-2xl font-bold mt-1">{t("inventoryTitle")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              const menu = window.confirm("OK = Share as text (copy to clipboard). Cancel = Download as image (PNG).");
              if (menu) {
                try {
                  const r = await api.get("/catalog.txt");
                  const res = await shareToWhatsApp({ text: r.data.text, title: "Product catalog" });
                  if (res.via === "clipboard") toast.success("Catalog copied — paste in WhatsApp 📋");
                } catch { toast.error("Couldn't fetch catalog"); }
              } else {
                // PNG download route — direct browser download of /api/catalog.png
                try {
                  const BASE = process.env.REACT_APP_BACKEND_URL || "";
                  const legacyToken = localStorage.getItem("kudi_token");
                  const headers = legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
                  const resp = await fetch(`${BASE}/api/catalog.png`, { headers, credentials: "include" });
                  if (!resp.ok) throw new Error("failed");
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `kobolog-catalog-${new Date().toISOString().slice(0, 10)}.png`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Catalog PNG downloaded — attach to WhatsApp 📸");
                } catch { toast.error("Couldn't generate catalog image"); }
              }
            }}
            className="bg-white border border-kudi-green text-kudi-green rounded-full px-3 py-2 text-xs font-semibold flex items-center gap-1 hover:bg-kudi-green hover:text-white transition"
            data-testid="share-catalog-btn"
            title="Share your product catalog on WhatsApp — text or image"
          >
            <Share2 className="w-3.5 h-3.5" /> Catalog
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-kudi-green text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5 hover:bg-kudi-green-dark transition"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" /> {t("addBtn")}
          </button>
        </div>
      </div>

      {data.low_stock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="low-stock-alert">
          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm text-amber-900">{t("restockTime")}</div>
            <div className="text-xs text-amber-800 mt-0.5">
              {data.low_stock.map((i) => i.name).join(", ")} {data.low_stock.length > 1 ? "are" : "is"} running low.
            </div>
          </div>
        </div>
      )}

      <PurchaseSuggestions suggestions={suggestions} />

      {data.items.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent text-kudi-green flex items-center justify-center mx-auto mb-3">
            <Package className="w-6 h-6" />
          </div>
          <div className="font-heading font-semibold">{t("noStockYet")}</div>
          <p className="text-sm text-kudi-ink/60 mt-1 max-w-xs mx-auto">
            {t("noStockHint")}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="inventory-list">
          {data.items.map((it) => {
            const low = it.stock <= it.restock_threshold;
            return (
              <div key={it.id} className="bg-white border border-border rounded-2xl p-3 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-heading font-bold ${low ? "bg-amber-50 text-amber-700" : "bg-accent text-kudi-green"}`}>
                  {it.name[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium capitalize truncate">{it.name}</div>
                  <div className="text-xs text-kudi-ink/55 mt-0.5">
                    {it.sell_price > 0 && <span>{fmtNaira(it.sell_price)} per {it.unit} · </span>}
                    {low && <span className="text-amber-700 font-semibold">{t("lowStock")}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setForecastFor(it)} className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center hover:bg-indigo-100" data-testid={`forecast-${it.name}`} title="View sales forecast">
                    <LineChart className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => updateStock(it.id, -1)} className="w-7 h-7 rounded-lg bg-accent text-kudi-ink/70 flex items-center justify-center hover:bg-accent/70" data-testid={`dec-${it.name}`}>
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <div className="font-heading font-bold tabular-nums min-w-[2rem] text-center">{it.stock}</div>
                  <button onClick={() => updateStock(it.id, 1)} className="w-7 h-7 rounded-lg bg-kudi-green text-white flex items-center justify-center hover:bg-kudi-green-dark" data-testid={`inc-${it.name}`}>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddProductModal t={t} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      {forecastFor && <ItemForecastModal item={forecastFor} onClose={() => setForecastFor(null)} />}
    </div>
  );
}

function AddProductModal({ t, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", unit: "bag", stock: 0, sell_price: 0, restock_threshold: 5 });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Enter product name"); return; }
    setBusy(true);
    try {
      await api.post("/inventory", {
        ...form,
        stock: Number(form.stock),
        sell_price: Number(form.sell_price),
        restock_threshold: Number(form.restock_threshold),
      });
      toast.success("Product added");
      onAdded();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-product-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">{t("addProduct")}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center" data-testid="close-modal"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input label={t("productName")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="prod-name" placeholder="e.g. rice" />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t("unit")} value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testid="prod-unit" placeholder="bag" />
            <Input label={t("stockQty")} type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="prod-stock" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label={t("sellPrice")} type="number" value={form.sell_price} onChange={(v) => setForm({ ...form, sell_price: v })} testid="prod-price" />
            <Input label={t("restockAt")} type="number" value={form.restock_threshold} onChange={(v) => setForm({ ...form, restock_threshold: v })} testid="prod-restock" />
          </div>
          <button type="submit" disabled={busy} className="w-full bg-kudi-green text-white rounded-full py-3.5 font-semibold hover:bg-kudi-green-dark transition disabled:opacity-50" data-testid="submit-product">
            {busy ? "Adding..." : t("addProduct")}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-kudi-ink/70">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-accent border border-transparent rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/20 focus:border-kudi-green transition"
        data-testid={testid}
      />
    </div>
  );
}

const URGENCY_STYLES = {
  critical: { bg: "bg-rose-50", border: "border-rose-200", badge: "bg-rose-600 text-white", label: "Restock now" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-500 text-white", label: "Low soon" },
  low_stock: { bg: "bg-amber-50", border: "border-amber-100", badge: "bg-amber-400 text-white", label: "Below threshold" },
  untracked: { bg: "bg-indigo-50", border: "border-indigo-200", badge: "bg-indigo-500 text-white", label: "Not in stock" },
};

function PurchaseSuggestions({ suggestions }) {
  const list = suggestions?.suggestions?.filter((s) => ["critical", "warning", "low_stock", "untracked"].includes(s.urgency)) || [];
  if (!suggestions || list.length === 0) return null;

  const shareList = async () => {
    const lines = [`*🛒 Restock plan — ${new Date().toLocaleDateString("en-NG", { day: "numeric", month: "short" })}*`, ""];
    list.forEach((s, i) => {
      lines.push(`${i + 1}. ${s.name}${s.unit && s.unit !== "unit" ? ` (${s.unit})` : ""} — ${s.recommended_reorder_qty || "?"} units${s.estimated_reorder_cost ? ` · ~₦${Number(s.estimated_reorder_cost).toLocaleString()}` : ""}`);
    });
    lines.push("", "_Auto-generated by Kobolog AI · kobolog.com.ng_");
    const text = lines.join("\n");
    try {
      const res = await shareToWhatsApp({ text, title: "Restock plan" });
      if (res.via === "clipboard") toast.success("Restock list copied 📋");
    } catch {
      await navigator.clipboard.writeText(text);
      toast.success("Restock list copied 📋");
    }
  };

  return (
    <div className="bg-gradient-to-br from-kudi-green/5 to-emerald-50 border border-kudi-green/20 rounded-2xl p-4 space-y-3" data-testid="purchase-suggestions">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-kudi-green">
            <TrendingUp className="w-3.5 h-3.5" />
            AI restock plan
          </div>
          <div className="font-heading text-base font-bold mt-0.5">
            {suggestions.summary.action_items} item{suggestions.summary.action_items === 1 ? "" : "s"} need restocking
          </div>
          {suggestions.summary.total_estimated_reorder_cost > 0 && (
            <div className="text-xs text-kudi-ink/60 mt-0.5">
              Est. spend: <span className="font-bold text-kudi-ink">{fmtNaira(suggestions.summary.total_estimated_reorder_cost)}</span>
            </div>
          )}
        </div>
        <button
          onClick={shareList}
          className="text-xs bg-white border border-kudi-green text-kudi-green px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1 hover:bg-kudi-green hover:text-white transition"
          data-testid="share-restock-plan"
        >
          <Copy className="w-3 h-3" /> Copy list
        </button>
      </div>

      <div className="space-y-1.5">
        {list.slice(0, 6).map((s, i) => {
          const style = URGENCY_STYLES[s.urgency] || URGENCY_STYLES.warning;
          return (
            <div
              key={`${s.item_id || s.name}-${i}`}
              className={`rounded-xl border ${style.border} ${style.bg} p-2.5 flex items-center gap-2.5`}
              data-testid={`suggestion-${s.name}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm capitalize truncate">{s.name}</div>
                <div className="text-[11px] text-kudi-ink/60 mt-0.5">
                  {s.avg_daily_sales > 0 && <>Sells ~{s.avg_daily_sales}/day · </>}
                  {s.days_of_stock_left !== null && s.days_of_stock_left !== undefined
                    ? (s.days_of_stock_left <= 0
                        ? <span className="text-rose-700 font-semibold">out of stock</span>
                        : <>{s.days_of_stock_left}d of stock left</>)
                    : "no recent sales"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${style.badge}`}>{style.label}</div>
                {s.recommended_reorder_qty > 0 && (
                  <div className="text-xs font-bold mt-1 tabular-nums">Buy {s.recommended_reorder_qty}</div>
                )}
              </div>
            </div>
          );
        })}
        {list.length > 6 && (
          <div className="text-[11px] text-center text-kudi-ink/50">+{list.length - 6} more suggestions</div>
        )}
      </div>
    </div>
  );
}


function ItemForecastModal({ item, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/insights/item-forecast/${item.id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load forecast"))
      .finally(() => setLoading(false));
  }, [item.id]);

  const maxHistory = data ? Math.max(1, ...data.history.map((h) => h.qty)) : 1;
  const maxForecast = data ? Math.max(1, ...data.forecast.map((f) => f.projected_qty)) : 1;
  const scale = Math.max(maxHistory, maxForecast, 1);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="forecast-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <header className="p-4 border-b border-border flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><LineChart className="w-4 h-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-kudi-ink/50 uppercase tracking-wider font-semibold">Sales forecast</div>
            <div className="font-heading font-bold text-base capitalize truncate">{item.name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-kudi-cream flex items-center justify-center" data-testid="forecast-close">✕</button>
        </header>

        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-kudi-ink/40">Loading forecast…</div>
          ) : !data ? (
            <div className="p-8 text-center text-kudi-ink/50">No data</div>
          ) : (
            <>
              {/* Headline stats */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <StatMini label="Current stock" value={data.item.current_stock} sub={data.item.unit} />
                <StatMini label="Avg / day" value={data.avg_daily_qty} sub="units" />
                <StatMini
                  label="Runs out"
                  value={data.projected_stockout_date ? new Date(data.projected_stockout_date).toLocaleDateString("en-NG", { day: "2-digit", month: "short" }) : "—"}
                  sub={data.projected_stockout_date ? "at pace" : "safe"}
                  tone={data.projected_stockout_date ? "rose" : "emerald"}
                />
              </div>

              {/* 28-day history bars */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-kudi-ink/60 uppercase tracking-wider mb-2">Last 28 days sold</div>
                <div className="flex items-end gap-0.5 h-16">
                  {data.history.map((h) => (
                    <div key={h.date} className="flex-1 flex flex-col items-center justify-end" title={`${h.date}: ${h.qty}`}>
                      <div
                        className="w-full bg-kudi-green rounded-sm"
                        style={{ height: `${(h.qty / scale) * 100}%`, minHeight: h.qty > 0 ? 2 : 0 }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[9px] text-kudi-ink/40 mt-1">
                  <span>28d ago</span><span>today</span>
                </div>
              </div>

              {/* 7-day forecast bars */}
              <div>
                <div className="text-xs font-semibold text-kudi-ink/60 uppercase tracking-wider mb-2">
                  Next 7 days · projected {data.projected_7d_qty} units
                </div>
                <div className="space-y-1.5">
                  {data.forecast.map((f) => (
                    <div key={f.date} className="flex items-center gap-2 text-xs">
                      <span className="w-8 text-kudi-ink/60">{f.weekday}</span>
                      <div className="flex-1 h-3 rounded-full bg-kudi-cream overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(f.projected_qty / scale) * 100}%` }} />
                      </div>
                      <span className="w-10 text-right font-semibold tabular-nums">{f.projected_qty}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value, sub, tone }) {
  const t = { rose: "text-rose-700", emerald: "text-emerald-700" }[tone] || "text-kudi-ink";
  return (
    <div className="bg-kudi-cream/60 rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-kudi-ink/50 font-semibold">{label}</div>
      <div className={`text-base font-bold tabular-nums ${t}`}>{value}</div>
      {sub && <div className="text-[10px] text-kudi-ink/50">{sub}</div>}
    </div>
  );
}

